import { assign, createMachine } from 'xstate';
import { DonationProvider } from '@joindeed/calculate-fees';

import { Privileges } from 'utils/privileges';
import OrganizationApi from 'entities/organization/api';
import Organization, { NonprofitEntityType } from 'entities/organization/model';
import { PaymentStep } from 'components/Flow/flows/nonprofitOnboardingPayment/components/PaymentStep';

import { PayPalWelcome } from './components/PayPalWelcome';
import { Loading } from './components/Loading';
import { PayPalSuccess } from './components/PayPalSuccess';
import { PayPalFailure } from './components/PayPalFailure';
import { WaitForApproval } from './components/WaitForApproval';
import { Gov } from './components/Gov';
import { ProofOfAssociationUpload } from './components/ProofOfAssociationUpload';
import { WhyNoPPB } from './components/WhyNoPPB';
import { NoCreds } from './components/NoCreds';
import { NoDesire } from './components/NoDesire';

export interface VerificationMachineContext {
  privileges: Privileges[];
  nonprofit: {
    donationProvider: DonationProvider | null;
    donationProviders?: Record<DonationProvider, boolean>;
    isPotentiallyNfgEligible: boolean;
    isPayPalEnrolled: boolean;
    nonprofitEntityType: NonprofitEntityType;
    isInBMF: boolean;
    isVerified: boolean;
    isChapter: boolean;
    isParentClaimed: boolean;
    governmentInformationLetterUploaded: boolean;
  };
  proofOfAssociationUploaded?: boolean;
  payPalVerificationCode?: string;
  verifyWithPayPalServiceErrorCode?: string;
  verifyWithPayPalServiceNamesAreSimilar?: boolean;
  verifyWithPayPalServiceBusinessName?: string;
  verifyWithPayPalService: () => Promise<{
    privileges: Privileges[];
    nonprofit: Partial<Organization>;
  }>;
  standaloneMode?: boolean;
}

export type VerificationMachineEvents =
  | { type: 'SUBMIT'; companies: string[] }
  | {
      type: 'PERSIST_LETTER';
      governmentInformationLetterUri: string;
      letterOfAssociationUri: string;
      legalName: string;
    }
  | { type: 'PREV' }
  | { type: 'NEXT' }
  | { type: 'ENROLL'; location: string }
  | { type: 'DISMISS' }
  | { type: 'NAVIGATE'; location: string }
  | { type: 'FOLLOW'; location: string }
  | { type: 'RETRY' }
  | { type: 'NO_PPB' }
  | { type: 'OPTION_FORBIDDEN' }
  | { type: 'OPTION_NO_CREDS' }
  | { type: 'OPTION_NO_DESIRE' }
  | { type: 'REQUEST_VERIFICATION'; supportingDocuments: string[]; role: string }
  | { type: 'SKIP' };

export const verificationMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QDcwCcCWAzDBjAhgC4YD2AdgHQZkbH4A2AxANoAMAuoqAA4my2kyXEAA9EARgBsAdgoAWAEziAnAGZpkgBwBWKZtYKANCACeEuXIrS5y5dPELpmxdsnaAvu+OpMOAsXIqGjomZnFOJBBefgChSLEEcScKcQN7XTlxfQVtbWMzRNZWSQpWbWklBXVJR2dPb3RsPCJBIIEGFgUInj4BcmEEqW0U1nFtTWdWaWnJZTl8iVtVK2UlaaTi1QVNepAfJv9W6nbQ1W6o3tiBxeHUsYm5KZm5hcTdBQplKWlWOU01SSZXb7PwtQLHEIsOTnaJ9OKgBJbZbSZRuJJfORbfTzUyIVQ1CjlGxyLQqCqKYGNUGxNqQ5jaGGXQTXRLSW6jcaTaYaF64xLOSwoqpZAzqJQeLx7KnNGkQjAdZiSRkxZnxCSqdn3LnPHEFJSqcQrJSPMY5bQkym+GVHYLy0LSZVwlniOSazmPbmzXWIBSsHQrFSu6a2BQyS0HMGUOUKzSOq5qhA2D4KFN+uaafFzcSvcSqZYWcqqP5qRx2STh6k2k4sZRx1UIvEGUqsLYaXTWGRSV4pkpfHJqL6qTQ-IcV62Bbj4EyT+gUMBkNAkehMADKAGkAJIABTYdf6CaLcwomlzU1+2lDBsk3Zqsg9RUyFUBjjHhwnU5nc4XS6YAFEAHIAEoAPIADKgbuwiwvGDYIAoJLDNIRbaBquSqGoeR8vBLoUOoLaZCojiSJIqivpGFCTtODBfouy4sBwUFMvusGhpkpSaCRrrEWMyiaN2uiGmMMiSAYuSPKiZE0pRn4AO5gPQuAkAAtmAjArgAqgAQgAshuAAqkGRNB9aiI26GEth2iigamhGFhKisBQNS-OouQaGMkmtNJ1FyQpymqeu26GT0KrMaZCCHpYJ6qGerqXl2WFFpYeHbBecgosOnnvlRs6+YpKmMP+wEAPpblumnBRcoXwuFWTKI5rADloGbTMUOYyIhqx+khbIkaRkoguOlDebOIImNQUCMBA5BgEEyAkAA1rNg1vsNH7UWNE0INQ82rbulXGWFCQpuIhqKLxZSZFs9WYXqebLP86j4jUvpstIWVrTlFCbWQk3oIuaAUfQRBYCQaBKd90qrRR62jVS42-dtZC7ZG+0MUZTE1cdShnQoF3mrmeNlN2SSGro-zFLoWwyO9A1Q+RI0ULAACuuC4HAsCFb+AAaBnoyFToHsSx6nj8cUkQld1HsOvyoj2b0fTDX1YPgGD0MzaCqYBv56YBACaB2YyyrGGn6nGuJIPF8VhvqOfB5r1TFVRTIrjMq2rGuqQAYmBoHAQA6ob1XGwhVjIahKEYd2jXKBQeM4yWeOuP1DRWtDZDs7AtJ2vRe5YxISHJuU-z-Fo7bXnyLoqMebIpie6Vy4rGdwNnCrhIxwcJkkWxx8XvGojoSQVwULpuDX5opheZT-HITeZxQMkABYmP+JBlZpjBbtrABqQeC7BubJC6UzpSO4zlDmiiWL8WSzLYxH-HPLdLyva-lYwwFbnpG7Af+xU+4BTSG4AAiwCAJ7xguFV0yxfhaBLKMFQJFXjKG0LHawthgw1GcLTVOEYaTNyzi-Ve68P5fx-n-IqxUADC2tgErggSZBIycnKuCqBMFMONpCXx7o+IookWphjpmnciBCF7L2Ie-T+39f7FUoWAlcG5tYMKOuqLQKQPSnxbOfLhlcLzDF+CmL4KDFB4yflnMgJAqGawgJzLev5d78yqvvcKzC3A1CHLZE6SRL62U+FIeqp1ZhDB2EIvBrRREWOAXADAmtN472UfnRI6hNDqJPq1DUOgdEj3SpYCefpGpsldMoMxFFFwkCwMBLAABBWAsASC4HlLEdS3B6AkHwBAVuTA7EOLzs6U6sh4IphRHoJwjgcz4lkGyAwJFQzmhQgoEp3AykVOqbU+pjTBDNNae0zpjBtYAEV1K-hXHpYq29fyAQ3F7DcVCqnSP-AklkqCPhJiqLYEkjgqg5ngrIPhRRHCKCeMU0JlZAiiKWSQcplSal1IaZGLZbSOkswAEZKVoMQX6U0ZpzUWstem+D54Qqhas2FGzyAIp2SitFhAMVQCRijWIaNeld0Lr3Jw-cy5DxzFIa+JI2TOB0BkcQizlnQrWXCppLTEVM2Zqi9FE1GD-TBkDEGYMIYrREYS0VJL1nwqlZS2V1LaX0vqajDgjyEyW0clMsoLYUSrGsNyv4nwMG8VcBUDiOCpTCIJS3IlKyYW6sldsjp7t1axIOUck5ZyLlXJuXc8hFrYLPPkKsN5cxbxfMrog1NdgvgaDzE8Ju5ArLiFwKoAA4iQZAFAoDVvQGQFSZBCAbjIKDcGkZQJgBpegClHToxdIuQo6NoEdZ6QuUm8KlsXkiVRIof48ELCvBQg1Phbz8RIhTt6sJYKS2jHLVWmtdafCNvnC2ttarO3dsIL2-V-bbQKkcYdRJuYiyfGKLUD5Q4tivDcB8WwtgyhJGDECEFQ0KAWLIKWg91ba31rQKe5trb21KSvT2tAfaZVyppQq6aZBZo7VxZDH14S91lsrbB49Dam3npQ2hm9GG71YaNVtQje1zVPqNl3PMlh6q3mcO4jMdkCgkkcheQ8zVQy1GLVB-dFGj3wcQ7Ry9sQu3ocw1S+VmKlWAxaaq8GxGd2UEg9B+TcGT00eQypwQamGMacNVpulbGzXsEnQkad8hZ3PgXRYb0iYe58IcLxJOhMZOmcPeZ6jZ6rMdtU9e29IaKBhs9pvIdG4R1jonZxzusEPOibndsVYvnXgN0JEUBwTs0KYjC3JiL-qxWkr1YlgdcT7FucQOlGBQlk7TOIm1Pk9qrClqcBbKyEwavkbq9qwNErNlMZa5G45pzzmXOubc+57X+QOCsKGWynqbqoOXXYOONhUT8umOhCbMGa31Z1bN8l82H2hGy8446Q4XkaBRC2Zw5RswDd9L3Io0V9AqF0Fdszt2ZtkrIPZ7DtKsX4ZxUtQzoLjNkeu6UyFAbxXQ9hyxxGznGUceZSxd78hPuOx+-YZdOE8ZBPsFUYiRZwdTaxw1oNc3EuaZw9ptAAMVWEBQyj8DJnauwchzjpr0rufGsJ4IJlHdXsSAzI5Xi6UpA1DsJsErthAdTCLPoVY42wPp3RxD6bkvg3SuSxG38hylsxtW-GjbL3IGDBPB8J8e2ZAHeUMukkQ2ijEWEnMWwisZKq0IF7MGVTuAQuQI+kn4VtiOBSKwwuQWlDLvNE5KYuZhxClRP1SUFiIBwGEBqyBz6WQAFph6IBr6bCePIlCAgcGyRWA7Fdu468J8wHwrIPlllUGwbhXaw274w5XvjGroWauoH49e4IIKNHjDM5pHimJNwzWGNEfyT5UXBKyJQHC5lyFIE6rp+JjGPFUNy+eHBj+31JXfeV-IH8SXjVIgPLaD6CShbsW2KwaBDUXbA0L1SvLyXfaxGJMAXAWlD-FkK8a1edFBBnYoP3eyGQJyX0IcC8CYQEJ-XBVHJWT8H6KARAruNQBqJqCYBffrPULISwe2R4BfDMLYcfL6FmNmDmSgliUfXCeqFBLQJQaZW6H0VCGuX4TfE8OwDQTgz8G3MAPg5PKyZYLQaYDiS2CwC7QAooYAlCUArQcAsxFQxEVYUoEkR6X0U6IvV4YcIuDkO+c6PMEpLvDGHLWqP4MTA0dCAwMYFsP7bJeCQkQFHIFsYoFECUYgkXeeIhN+TSMwiQQxJya6VvbIbYbhYYXhf5cYaoCA-FcJeeCxKxSAeADwpXRMTESwuBLYBBOwyuTEFJQffUCoP0P4EpSJaJTWJIt4ZIVBdCQsVYS2BwHxZYBwXIfA0sF0WeZ-Iov1C3RrK3dpXoiwBqKw+BWwpBSuYoM6ADbYTIWyTIcsOYsFLVNnO7XHR7E4VYo8PwtEFCZwFBVQb5FCPxLQKYB1LQk4mI9Oc44lKHKXA1OHCaXojMKKR-P0HQTYMYblNkF1fuOKdgkVC4wE5Y0NVWcNZQionvRMCYGo6w+o7YkeMYZYPIqQU0DMYSFnatVY7bJwnrUMPrJfHIX5PhH4TxYcQRX4kRM3CLKjBDSzC9WLGzeLRjENVYkSUobrEiXrESJfJKMrUYWYFqJwA0GkhTCzaLYU1DOLdTa4kIXojIXCVBTIEkdCVEcQhAFCXsDXawKE0-aI7dEg0XSbSjRTIUujPUuzJjGXUEnEqfBAOYXJPhAVXw9CErVBZsIoH4XqVwVBJ0yA3dWTN0zUqLJDHU+jBLa3TEz2I0v0aU+M2Upk+UkrfEQkcE4iFBHieMjUzHAEy3TnRFXooYO2aKNxcYP4CoErU6QPCY+uCYOoU4tHFMjHCXJYpsnZdwgWXEjYSwGoKeMYdveuX9KYXCKZOwJ4xQbk50kXPk8XRYjnB7LnBzHnCggMw-X0XISw+wOwPIzIAAvkC8EocI0YNYjKJIOs8co8mHJjJQlslUuOds+MgVbsp8lsY8e6bBQEUYbYH43c6GCPWgaPNAWPePBgXo2ZXJc0DQWdc0aobPD4TJfxEfCoUMRWPDbEmcwMvCWQHQADEiKyOdFkosdQ30E0NYC-TwTwIAA */
    id: 'verification',
    key: 'verification',
    initial: 'initial',
    schema: {
      context: {} as VerificationMachineContext,
      events: {} as VerificationMachineEvents,
    },
    exit: 'handleFinal',
    states: {
      initial: {
        always: [
          {
            target: 'done',
            cond: 'verificationCompleted',
          },
          {
            target: 'paypal.verifying',
            cond: 'hasPayPalVerificationCode',
          },
          {
            target: 'nces',
            cond: 'isNCESSchool',
          },
          {
            target: 'non501c3Gov',
            cond: 'isNon501c3Gov',
          },
          {
            target: 'paypal.welcome',
            cond: 'isMembershipOrg',
          },
          {
            target: 'paypal',
            cond: 'isPayPal',
          },
          {
            target: 'paypal.welcome',
            cond: 'isNFG',
          },
          {
            target: 'paypal',
            cond: 'isPotentiallyNfgEligible',
          },
          {
            target: 'waitForApproval',
            cond: 'isChapter',
          },
          {
            target: 'waitForApproval',
          },
        ],
      },
      paypal: {
        initial: 'enroll',
        states: {
          enroll: {
            always: [
              {
                target: 'welcome',
                cond: 'isPayPalEnrolledOrStandalone',
              },
            ],
            on: {
              SKIP: {
                target: '#verification.done',
                cond: 'isUnblocked',
              },
              ENROLL: {
                target: 'enroll',
                actions: ['openExternalLink'],
              },
            },
            meta: {
              component: PaymentStep,
            },
          },
          welcome: {
            on: {
              SUBMIT: {
                target: 'redirecting',
              },
              SKIP: {
                target: '#verification.done',
                cond: 'isUnblocked',
              },
              NO_PPB: {
                target: '#verification.nces.whyNoPPB',
              },
            },
            meta: {
              component: PayPalWelcome,
            },
          },
          redirecting: {
            entry: ['redirectToPayPalVerification'],
            meta: {
              component: Loading,
            },
          },
          verifying: {
            invoke: {
              src: 'verifyWithPayPalService',
              onDone: {
                target: 'success',
                actions: assign({
                  privileges: (_context, event) => event.data.privileges,
                  nonprofit: (_context, event) => event.data.nonprofit,
                }),
              },
              onError: {
                target: 'failure',
                actions: assign({
                  verifyWithPayPalServiceErrorCode: (_context, event) =>
                    event.data?.graphQLErrors?.[0]?.extensions?.code,
                  verifyWithPayPalServiceNamesAreSimilar: (_context, event) =>
                    event.data?.graphQLErrors?.[0]?.extensions?.namesAreSimilar,
                  verifyWithPayPalServiceBusinessName: (_context, event) =>
                    event.data?.graphQLErrors?.[0]?.extensions?.payPalBusinessName,
                }),
              },
            },
            meta: {
              component: Loading,
            },
          },
          success: {
            on: {
              NEXT: '#verification.done',
            },
            meta: {
              component: PayPalSuccess,
            },
          },
          failure: {
            on: {
              RETRY: { target: 'welcome' },
              FOLLOW: {
                target: 'failure',
                actions: ['openExternalLink'],
              },
            },
            meta: {
              component: PayPalFailure,
            },
          },
        },
      },
      nces: {
        initial: 'initial',
        states: {
          initial: {
            always: [
              {
                target: '#verification.waitForApproval',
                cond: 'proofOfAssociationUploaded',
              },
              {
                target: '#verification.paypal.welcome',
              },
            ],
          },
          whyNoPPB: {
            on: {
              PREV: {
                target: 'initial',
              },
              OPTION_FORBIDDEN: {
                target: 'proofOfAssociationUpload',
              },
              OPTION_NO_CREDS: {
                target: 'noCreds',
              },
              OPTION_NO_DESIRE: {
                target: 'noDesire',
              },
            },
            meta: {
              component: WhyNoPPB,
            },
          },
          noCreds: {
            on: {
              PREV: {
                target: 'whyNoPPB',
              },
            },
            meta: {
              component: NoCreds,
            },
          },
          noDesire: {
            on: {
              PREV: {
                target: 'whyNoPPB',
              },
            },
            meta: {
              component: NoDesire,
            },
          },
          proofOfAssociationUpload: {
            initial: 'initial',
            states: {
              initial: {
                on: {
                  PREV: {
                    target: '#verification.nces.whyNoPPB',
                  },
                  REQUEST_VERIFICATION: {
                    target: 'submitting',
                  },
                },
              },
              submitting: {
                invoke: {
                  src: 'requestVerification',
                  onDone: {
                    target: '#verification.waitForApproval',
                    actions: [
                      assign({
                        proofOfAssociationUploaded: () => true,
                      }),
                      'setProofOfAssociation',
                    ],
                  },
                  onError: {
                    target: 'failure',
                  },
                },
              },
              failure: {
                on: {
                  REQUEST_VERIFICATION: 'submitting',
                },
              },
            },
            meta: {
              component: ProofOfAssociationUpload,
            },
          },
        },
      },
      non501c3Gov: {
        initial: 'governmentInformationLetterUpload',
        states: {
          governmentInformationLetterUpload: {
            initial: 'initial',
            states: {
              initial: {
                always: [
                  {
                    target: '#verification.non501c3Gov.proofOfAssociationUpload',
                    cond: 'governmentInformationLetterUploaded',
                  },
                ],
                on: {
                  PERSIST_LETTER: 'submitting',
                },
              },
              submitting: {
                invoke: {
                  src: 'persistGovernmentInformationLetter',
                  onDone: {
                    target: '#verification.non501c3Gov.proofOfAssociationUpload',
                    actions: assign({
                      nonprofit: (context) => ({
                        ...context.nonprofit,
                        governmentInformationLetterUploaded: true,
                      }),
                    }),
                  },
                  onError: {
                    target: 'failure',
                  },
                },
              },
              failure: {
                on: {
                  PERSIST_LETTER: 'submitting',
                },
              },
            },
            meta: {
              component: Gov,
            },
          },

          proofOfAssociationUpload: {
            initial: 'initial',
            states: {
              initial: {
                always: [
                  {
                    target: '#verification.waitForApproval',
                    cond: 'proofOfAssociationUploaded',
                  },
                ],
                on: {
                  PREV: {
                    target: '#verification.non501c3Gov.governmentInformationLetterUpload',
                  },
                  REQUEST_VERIFICATION: {
                    target: 'submitting',
                  },
                },
              },
              submitting: {
                invoke: {
                  src: 'requestVerification',
                  onDone: {
                    target: '#verification.waitForApproval',
                    actions: [
                      assign({
                        proofOfAssociationUploaded: () => true,
                      }),
                      'setProofOfAssociation',
                    ],
                  },
                  onError: {
                    target: 'failure',
                  },
                },
              },
              failure: {
                on: {
                  REQUEST_VERIFICATION: 'submitting',
                },
              },
            },
            meta: {
              component: ProofOfAssociationUpload,
            },
          },
        },
      },
      waitForApproval: {
        always: [
          {
            target: 'done',
            cond: 'isUnblocked',
          },
        ],
        meta: {
          component: WaitForApproval,
          props: {
            noWelcome: true,
          },
        },
      },
      done: {
        type: 'final',
      },
    },
    tsTypes: {} as import('./machine.typegen').Typegen0,
  },
  {
    actions: {
      openExternalLink: (_context, event): void => {
        if (event.location) {
          window.open(event.location, '_blank')?.focus();
        }
      },
    },
    guards: {
      // @TODO-DP: find a better way to check whether the user is blocked
      isUnblocked: (context) => context.privileges.length > 1 && !context.standaloneMode,
      isPayPal: (context) => context.nonprofit.donationProvider === DonationProvider.PayPal,
      // @NOTE-DP: is NFG eligible according to NFG api
      isNFG: (context) => Boolean(context.nonprofit?.donationProviders?.NFG),
      // @NOTE-DP: is potentially NFG eligible (i.e. is US + has ein or NCES identifier)
      isPotentiallyNfgEligible: (context) => context.nonprofit.isPotentiallyNfgEligible,
      governmentInformationLetterUploaded: (context) =>
        context.nonprofit.governmentInformationLetterUploaded,
      proofOfAssociationUploaded: (context) => Boolean(context.proofOfAssociationUploaded),
      hasPayPalVerificationCode: (context) => Boolean(context.payPalVerificationCode),
      verificationCompleted: (context) =>
        !(
          context.privileges.includes(Privileges.ORGANIZATION_SELF_VERIFY_OWNERSHIP) &&
          !context.privileges.includes(Privileges.ORGANIZATION_SELF_VERIFIED)
        ),
      isPayPalEnrolledOrStandalone: (context) =>
        context.nonprofit.isPayPalEnrolled || Boolean(context.standaloneMode),
      isNon501c3Gov: (context) =>
        context.nonprofit.nonprofitEntityType === NonprofitEntityType.governmentEntity,
      isMembershipOrg: (context) =>
        context.nonprofit.nonprofitEntityType === NonprofitEntityType.memberAssociatedEntity,
      isNCESSchool: (context) =>
        context.nonprofit.nonprofitEntityType === NonprofitEntityType.school,

      isChapter: (context) => context.nonprofit.isChapter,
    },
    services: {
      requestVerification: async (_context, event) => {
        if (event.type === 'REQUEST_VERIFICATION') {
          await OrganizationApi.requestVerification(
            event.supportingDocuments,
            event.role
          ).toPromise();
        }
      },
      persistGovernmentInformationLetter: async (_context, event) => {
        if (event.type === 'PERSIST_LETTER' && event.governmentInformationLetterUri) {
          await OrganizationApi.persistGovernmentInformationLetter(
            event.governmentInformationLetterUri,
            event.letterOfAssociationUri,
            event.legalName
          ).toPromise();
        }
      },
      verifyWithPayPalService: async (context) => context.verifyWithPayPalService(),
    },
  }
);
